import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';

const sections = [
  {
    number: 'A',
    title: 'Not Professional Advice; Not an Expert Report',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>
          Each Report is an automated analytical output generated by artificial intelligence
          software and is provided for informational and preliminary analytical purposes only.
        </p>
        <p>A Report:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>is NOT legal advice</li>
          <li>is NOT engineering advice</li>
          <li>is NOT a licensed fire investigation opinion</li>
          <li>is NOT an insurance coverage determination</li>
          <li>is NOT an expert witness report</li>
          <li>is NOT a forensic determination of origin or cause</li>
        </ul>
        <p>Use of the Service does not create:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>an attorney-client relationship</li>
          <li>an expert-client relationship</li>
          <li>an insurer-insured relationship</li>
          <li>an engineering engagement</li>
          <li>a fiduciary relationship</li>
          <li>any other professional services relationship.</li>
        </ul>
        <p>
          Only appropriately licensed professionals conducting proper field investigations may
          provide authoritative fire origin and cause determinations.
        </p>
      </div>
    ),
  },
  {
    number: 'B',
    title: 'Not for Emergency Response or Life-Safety Decisions',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>
          FireCaseIQ and any Report generated by the Service must never be used for emergency
          response, life-safety decisions, or real-time hazard evaluation.
        </p>
        <p>
          If there is an emergency, contact appropriate emergency services immediately.
        </p>
        <p>The Service is not designed for real-time operational decision-making.</p>
      </div>
    ),
  },
  {
    number: 'C',
    title: 'Input Limitations and Scope of Analysis',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>Reports are generated solely from materials provided by the user.</p>
        <p>
          The system does not independently collect evidence and does not conduct any physical
          investigation.
        </p>
        <p>FireCaseIQ does not:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>conduct site inspections</li>
          <li>collect physical evidence</li>
          <li>perform destructive examination</li>
          <li>perform electrical testing or measurements</li>
          <li>perform laboratory analysis</li>
          <li>interview witnesses</li>
          <li>preserve physical evidence</li>
          <li>verify authenticity of submitted materials</li>
        </ul>
        <p>
          If relevant information is not visible in submitted materials, the Report may state
          "Not observable" or "Not provided."
        </p>
        <p>
          Users are responsible for ensuring the accuracy, legality, and completeness of all
          materials submitted to the Service.
        </p>
      </div>
    ),
  },
  {
    number: 'D',
    title: 'AI Limitations and Uncertainty',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>Artificial intelligence systems can produce outputs that are:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>inaccurate</li>
          <li>incomplete</li>
          <li>outdated</li>
          <li>inconsistent</li>
          <li>misleading</li>
        </ul>
        <p>
          AI systems may generate hallucinations, meaning statements that appear plausible but
          are incorrect or unsupported.
        </p>
        <p>
          Confidence scores, probability estimates, and analytical outputs generated by the
          Service are heuristic estimates only and do not represent scientific certainty or legal
          conclusions.
        </p>
        <p>
          Real-world outcomes depend on numerous factors outside the system's control, including:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>site conditions</li>
          <li>additional evidence</li>
          <li>jurisdictional rules</li>
          <li>witness credibility</li>
          <li>investigative methodology</li>
          <li>evidence preservation practices.</li>
        </ul>
        <p>
          Users must independently evaluate and verify all information contained in a Report.
        </p>
      </div>
    ),
  },
  {
    number: 'E',
    title: 'Standards References',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>Reports may reference industry frameworks, guidelines, or standards such as:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>NFPA 921</li>
          <li>NFPA 1033</li>
          <li>ASTM E2713</li>
          <li>NEC guidance</li>
          <li>NEMA guidance</li>
          <li>IEEE guidance</li>
          <li>UL guidance</li>
        </ul>
        <p>
          These references are used solely to structure analytical reasoning within the Report.
        </p>
        <p>References to such standards do not mean the Report:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>complies with those standards</li>
          <li>satisfies professional investigative requirements</li>
          <li>constitutes a certified or admissible expert report.</li>
        </ul>
      </div>
    ),
  },
  {
    number: 'F',
    title: 'No Reliance; Duty to Verify',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>
          Users must not rely exclusively on any Report when making legal, insurance,
          investigative, financial, or safety decisions.
        </p>
        <p>
          Before using a Report in any professional or legal context, Users must independently
          verify:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>factual statements</li>
          <li>cited standards</li>
          <li>analytical reasoning</li>
          <li>conclusions or observations.</li>
        </ul>
        <p>Users assume full responsibility for decisions made based on Reports.</p>
        <p>Qualified professionals should be consulted for definitive determinations.</p>
      </div>
    ),
  },
  {
    number: 'G',
    title: 'No Chain-of-Custody or Forensic Integrity Guarantee',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>FireCaseIQ does not provide evidence preservation services.</p>
        <p>The Service does not guarantee:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>forensic integrity</li>
          <li>chain-of-custody documentation</li>
          <li>evidence authenticity</li>
          <li>admissibility in court</li>
        </ul>
        <p>
          Users remain responsible for maintaining appropriate chain-of-custody procedures for
          physical or digital evidence.
        </p>
        <p>Original evidence should always be preserved outside the FireCaseIQ system.</p>
      </div>
    ),
  },
  {
    number: 'H',
    title: 'Optional Expert Review',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>
          FireCaseIQ may allow Users to engage independent experts to review Reports.
        </p>
        <p>
          Any expert engaged through the platform is an independent contractor and is solely
          responsible for their professional services, opinions, and deliverables.
        </p>
        <p>
          Grunt Logic does not supervise or control the professional judgment of any expert.
        </p>
      </div>
    ),
  },
  {
    number: 'I',
    title: 'Report Watermark and Disclosure Requirements',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>
          Each Report generated through the Service will include a visible notice indicating that
          the document is an AI-generated preliminary report subject to this Policy.
        </p>
        <p>
          Users agree not to remove, obscure, or modify any watermark, disclaimer, or disclosure
          notice before sharing Reports with third parties.
        </p>
      </div>
    ),
  },
  {
    number: 'J',
    title: 'Limitation of Liability',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p className="uppercase font-medium text-foreground text-xs tracking-wide">
          To the maximum extent permitted by law, Grunt Logic and its affiliates shall not be
          liable for any indirect, incidental, consequential, special, punitive, or exemplary
          damages arising out of or related to the use of the Service or any Report.
        </p>
        <p>
          To the maximum extent permitted by law, the total cumulative liability of Grunt Logic
          arising out of or relating to the Service or any Report shall not exceed the total
          amount paid by the User to FireCaseIQ during the twelve (12) months preceding the
          event giving rise to the claim.
        </p>
        <p>
          Some jurisdictions do not allow certain liability limitations; in such cases, liability
          will be limited to the maximum extent permitted by law.
        </p>
      </div>
    ),
  },
  {
    number: 'K',
    title: 'User Indemnification',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>
          Users agree to defend, indemnify, and hold harmless Grunt Logic, its affiliates,
          officers, employees, and contractors from any claims, damages, losses, liabilities,
          costs, or expenses (including reasonable attorneys' fees) arising from:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>misuse of the Service</li>
          <li>reliance on a Report without independent verification</li>
          <li>improper sharing or representation of Reports</li>
          <li>violation of applicable laws or regulations</li>
          <li>submission of unlawful or infringing materials.</li>
        </ul>
      </div>
    ),
  },
  {
    number: 'L',
    title: 'No Warranties',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p className="uppercase font-medium text-foreground text-xs tracking-wide">
          The Service and all Reports are provided "AS IS" and "AS AVAILABLE."
        </p>
        <p>
          Grunt Logic makes no warranties, express or implied, including warranties of:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>accuracy</li>
          <li>reliability</li>
          <li>completeness</li>
          <li>fitness for a particular purpose</li>
          <li>non-infringement</li>
          <li>uninterrupted service.</li>
        </ul>
        <p>Users assume all risk associated with use of the Service.</p>
      </div>
    ),
  },
  {
    number: 'M',
    title: 'Policy Updates',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>Grunt Logic may update this Policy periodically.</p>
        <p>
          The most current version will always be available through the FireCaseIQ platform.
        </p>
        <p>
          Continued use of the Service after updates constitutes acceptance of the revised Policy.
        </p>
      </div>
    ),
  },
];

const AccordionItem = ({ section, isOpen, onToggle }) => (
  <div className="border border-border rounded-lg overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between px-5 py-4 bg-card hover:bg-muted/50 transition-colors text-left group"
    >
      <div className="flex items-center gap-3">
        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">
          {section.number}
        </span>
        <span className="font-semibold text-foreground text-sm">{section.title}</span>
      </div>
      <Icon
        name={isOpen ? 'ChevronUp' : 'ChevronDown'}
        size={18}
        className="text-muted-foreground group-hover:text-foreground transition-colors flex-shrink-0 ml-3"
      />
    </button>
    {isOpen && (
      <div className="px-5 py-4 bg-background border-t border-border">
        {section.content}
      </div>
    )}
  </div>
);

const ReportDisclaimerPage = () => {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState(null);

  const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border py-4">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center">
            <img src="/assets/images/logo.png" alt="FireCaseIQ" className="h-10 object-contain" />
          </button>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="ArrowLeft" size={20} />
            Back
          </button>
        </div>
      </header>

      {/* Page content */}
      <main className="max-w-3xl mx-auto px-4 md:px-6 py-10 space-y-8">
        {/* Title block */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-foreground">AI-Generated Preliminary Report Policy</h1>
          <p className="text-sm text-muted-foreground">
            <span className="font-medium">Effective Date:</span> February 15, 2026
            &nbsp;&nbsp;|&nbsp;&nbsp;
            <span className="font-medium">Last Updated:</span> February 27, 2026
          </p>
          <p className="text-sm text-muted-foreground leading-relaxed pt-1">
            This Policy describes the disclosures, limitations, and conditions that apply to any
            AI-generated preliminary report produced through the FireCaseIQ platform (each, a
            "Report"). It applies to all users of FireCaseIQ, including Customers, Experts, and
            other authorized users, and is incorporated into the FireCaseIQ Terms of Service.
          </p>
          <p className="text-sm text-muted-foreground leading-relaxed">
            By accessing, generating, downloading, sharing, or otherwise using any Report, you
            acknowledge that you have read, understood, and agree to this Policy. FireCaseIQ is
            operated by Filmology LLC d/b/a Grunt Logic ("Grunt Logic," "FireCaseIQ," "we," "us,"
            or "our").
          </p>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Reports are generated using artificial intelligence systems, including large language
            models and third-party APIs (such as those provided by OpenAI), based solely on
            materials submitted by the user, which may include photographs, documents, field notes,
            metadata, and responses to clarifying questions. FireCaseIQ does not independently
            verify, investigate, or validate user-submitted materials.
          </p>
        </div>

        {/* Accordion sections */}
        <div className="space-y-3">
          {sections.map((section, i) => (
            <AccordionItem
              key={section.number}
              section={section}
              isOpen={openIndex === i}
              onToggle={() => toggle(i)}
            />
          ))}
        </div>
      </main>
    </div>
  );
};

export default ReportDisclaimerPage;
